/**
 * Zone brand wordmark — "z●ne".
 *
 * Single source of truth for the logo across the admin console (sidebar,
 * login, session splash). Renders the official brand SVG shipped in
 * `public/brand/`, so the exact letterforms / dot are always correct.
 *
 * Color is chosen by `variant`:
 *   - "white" → white wordmark, for the dark admin UI (default)
 *   - "black" → black wordmark, for light backgrounds / print contexts
 *
 * Size is set with `height` (px); width scales from the artwork's
 * intrinsic 493×128 aspect ratio.
 */

// Files live in public/brand and are served from the site root. Spaces in
// the filenames are URL-encoded so the browser fetches them correctly.
const BRAND_SRC = {
  white: "/brand/zone%20logo%20white.svg",
  black: "/brand/zone%20logo%20black.svg",
} as const;

// Intrinsic artwork aspect ratio (viewBox 0 0 493 128).
const ART_W = 493;
const ART_H = 128;

export type ZoneWordmarkVariant = keyof typeof BRAND_SRC;

export interface ZoneWordmarkProps {
  /** Rendered height in px. Width scales with the aspect ratio. */
  height?: number;
  /** Color variant. "white" for dark UI (default), "black" for light bg. */
  variant?: ZoneWordmarkVariant;
  className?: string;
  /** Accessible label. */
  title?: string;
}

export function ZoneWordmark({
  height = 22,
  variant = "white",
  className,
  title = "Zone",
}: ZoneWordmarkProps) {
  const width = Math.round((height * ART_W) / ART_H);
  return (
    <img
      src={BRAND_SRC[variant]}
      width={width}
      height={height}
      className={className}
      alt={title}
      draggable={false}
      style={{ display: "block" }}
    />
  );
}
